/**
 * Timeline edits as pure functions on the video plan — split, duplicate, paste, delete, move, trim, slip, and the
 * soundtrack's own edits. Every edit returns a new plan (undo/redo keep the old ones) and re-derives what depends on
 * the cut: the creator's captions follow their clip, and the music dips under the lines that are heard.
 */
import { VIDEO_FPS, sceneStarts, totalFrames, voicePartsOf, type Caption, type TextLayer, type TranscriptSource, type VideoAudio, type VideoMedia, type VideoScene, type VideoSpec, type VoicePart } from '@/video/spec';

export const MIN_SCENE = 0.4;
const MAX_SCENE = 30;
const r2 = (n: number) => Math.round(n * 100) / 100;
let counter = 0;
export const newSceneId = (base: string) => `${base.replace(/-[a-z0-9]{4,}$/, '')}-${Date.now().toString(36).slice(-4)}${(counter++).toString(36)}`;

/** A scene that plays a stretch of a source video (a clip, or a hook over live/blurred footage). */
export const onFootage = (s: VideoScene): boolean => (s.kind === 'clip' || s.kind === 'hook') && s.media?.kind === 'video';
const mediaOf = (s: VideoScene) => ('media' in s ? s.media || null : null);

/** Seconds where each scene starts in the finished video. */
export const startsSec = (spec: VideoSpec): number[] => sceneStarts(spec).map((f) => f / VIDEO_FPS);
export const lengthSec = (spec: VideoSpec): number => totalFrames(spec) / VIDEO_FPS;

/** The scene playing at `t` seconds (the later one inside a transition overlap). */
export function sceneAt(spec: VideoSpec, t: number): number {
  const st = startsSec(spec);
  let i = 0;
  st.forEach((s, k) => { if (t >= s - 1e-6) i = k; });
  return i;
}

function shiftCaptions(caps: Caption[] | undefined, by: number, length: number): Caption[] | undefined {
  if (!caps) return caps;
  return caps.map((c) => ({ ...c, start: r2(c.start - by), end: r2(c.end - by) }))
    .filter((c) => c.end > 0.15 && c.start < length - 0.15)
    .map((c) => ({ ...c, start: Math.max(0, c.start), end: Math.min(length, c.end) }));
}

/** Music dips under every caption line of a scene whose creator is heard (lines < 0.6 s apart share one dip). */
export function duckSpans(spec: VideoSpec): NonNullable<VideoAudio['duck']> {
  const st = startsSec(spec);
  const spans: Array<{ start: number; end: number }> = [];
  spec.scenes.forEach((s, i) => {
    if (!onFootage(s) || (s as { voice?: string }).voice !== 'keep') return;
    for (const c of (s.kind === 'clip' ? s.captions : undefined) || []) spans.push({ start: r2(st[i] + c.start), end: r2(st[i] + Math.min(s.duration, c.end)) });
    if (s.kind === 'hook') spans.push({ start: r2(st[i]), end: r2(st[i] + s.duration) });
  });
  for (const v of voicePartsOf(spec.audio)) spans.push({ start: r2(v.at), end: r2(v.at + v.seconds) });
  return spans.sort((a, b) => a.start - b.start).reduce<typeof spans>((acc, d) => { const l = acc[acc.length - 1]; if (l && d.start - l.end < 0.6) l.end = Math.max(l.end, d.end); else acc.push({ ...d }); return acc; }, []);
}

/** After any cut change: the dips follow the heard lines (unless the person switched dipping off). */
export function reflow(spec: VideoSpec): VideoSpec {
  const a = spec.audio;
  if (!a?.musicUrl || a.duck === undefined) return spec;
  return { ...spec, audio: { ...a, duck: duckSpans(spec) } };
}

const withScenes = (spec: VideoSpec, scenes: VideoScene[]): VideoSpec => reflow({ ...spec, scenes });

/** Cut the scene at `t` seconds into it: footage continues where the first half stops; captions go with their half. */
export function splitScene(spec: VideoSpec, i: number, t: number): VideoSpec | null {
  const s = spec.scenes[i];
  if (!s || t < MIN_SCENE || s.duration - t < MIN_SCENE) return null;
  const a = { ...s, duration: r2(t) } as VideoScene;
  const b = { ...s, id: newSceneId(s.id), duration: r2(s.duration - t), transition: 'cut' } as VideoScene;
  const m = mediaOf(s);
  if (m?.kind === 'video') (b as { media: typeof m }).media = { ...m, trimStart: r2((m.trimStart || 0) + t * (m.playbackRate || 1)) };
  if (s.kind === 'clip') {
    (a as typeof s).captions = shiftCaptions(s.captions, 0, t);
    (b as typeof s).captions = shiftCaptions(s.captions, t, s.duration - t);
  }
  // The second half of a hook keeps playing the footage as a clip (one hook per video).
  const second: VideoScene = s.kind === 'hook' && m?.kind === 'video'
    ? { id: b.id, kind: 'clip', duration: b.duration, transition: 'cut', media: (b as { media: typeof m }).media, voice: s.voice, captions: [] }
    : b;
  const scenes = [...spec.scenes];
  scenes.splice(i, 1, a, second);
  return withScenes(spec, scenes);
}

export function insertScene(spec: VideoSpec, scene: VideoScene, at: number): VideoSpec {
  const copy = JSON.parse(JSON.stringify(scene)) as VideoScene;
  copy.id = newSceneId(scene.id);
  // A pasted hook anywhere but the start becomes a statement (one hook per video).
  const placed: VideoScene = copy.kind === 'hook' && at > 0 ? { id: copy.id, kind: 'text', text: copy.text, sub: copy.sub, duration: copy.duration, transition: 'cut', tone: copy.tone } : copy;
  const scenes = [...spec.scenes];
  scenes.splice(Math.max(0, Math.min(at, scenes.length)), 0, placed);
  return withScenes(spec, scenes);
}

export const duplicateScene = (spec: VideoSpec, i: number): VideoSpec => insertScene(spec, spec.scenes[i], i + 1);

export function deleteScene(spec: VideoSpec, i: number): VideoSpec | null {
  if (spec.scenes.length <= 1) return null;
  const scenes = spec.scenes.filter((_, k) => k !== i);
  if (i === 0 && scenes[0]) scenes[0] = { ...scenes[0], transition: 'cut' };
  return withScenes(spec, scenes);
}

export function moveScene(spec: VideoSpec, from: number, to: number): VideoSpec {
  if (from === to) return spec;
  const scenes = [...spec.scenes];
  const [s] = scenes.splice(from, 1);
  scenes.splice(Math.max(0, Math.min(to, scenes.length)), 0, s);
  scenes[0] = { ...scenes[0], transition: 'cut' };
  return withScenes(spec, scenes);
}

/** Drag the right edge: the scene lasts `duration` s. */
export function trimEnd(spec: VideoSpec, i: number, duration: number): VideoSpec {
  const s = spec.scenes[i];
  const d = r2(Math.max(MIN_SCENE, Math.min(MAX_SCENE, duration)));
  const next = { ...s, duration: d } as VideoScene;
  if (s.kind === 'clip') (next as typeof s).captions = shiftCaptions(s.captions, 0, d);
  return withScenes(spec, spec.scenes.map((x, k) => (k === i ? next : x)));
}

/** Drag the left edge by `delta` s (positive = later): footage starts later in the source, the scene gets shorter. */
export function trimStart(spec: VideoSpec, i: number, delta: number): VideoSpec {
  const s = spec.scenes[i];
  const m = mediaOf(s);
  const from = m?.kind === 'video' ? m.trimStart || 0 : 0;
  const dd = Math.max(m?.kind === 'video' ? -from / (m.playbackRate || 1) : -MAX_SCENE, Math.min(s.duration - MIN_SCENE, delta));
  const d = r2(Math.min(MAX_SCENE, s.duration - dd));
  const next = { ...s, duration: d } as VideoScene;
  if (m?.kind === 'video') (next as { media: typeof m }).media = { ...m, trimStart: r2(from + dd * (m.playbackRate || 1)) };
  if (s.kind === 'clip') (next as typeof s).captions = shiftCaptions(s.captions, dd, d);
  return withScenes(spec, spec.scenes.map((x, k) => (k === i ? next : x)));
}

export function setScene(spec: VideoSpec, i: number, patch: Partial<VideoScene>): VideoSpec {
  return withScenes(spec, spec.scenes.map((x, k) => (k === i ? ({ ...x, ...patch } as VideoScene) : x)));
}

export function setClipVolume(spec: VideoSpec, i: number, volume: number): VideoSpec {
  const s = spec.scenes[i];
  const m = mediaOf(s);
  if (!m) return spec;
  return setScene(spec, i, { media: { ...m, volume: r2(Math.max(0, Math.min(1, volume))) } } as Partial<VideoScene>);
}

export function setAudio(spec: VideoSpec, patch: Partial<VideoAudio>): VideoSpec {
  return { ...spec, audio: { ...(spec.audio || {}), ...patch } };
}

/** Dipping on: spans from the heard lines; off: none (the music keeps its level). */
export const setDucking = (spec: VideoSpec, on: boolean): VideoSpec => setAudio(spec, { duck: on ? duckSpans(spec) : undefined });

export function moveSfx(spec: VideoSpec, i: number, at: number): VideoSpec {
  const sfx = [...(spec.audio?.sfx || [])];
  if (!sfx[i]) return spec;
  const t = r2(Math.max(0, Math.min(lengthSec(spec) - 0.1, at)));
  sfx[i] = { ...sfx[i], at: t, ...(sfx[i].scene ? { scene: spec.scenes[sceneAt(spec, t)]?.id } : {}) };
  return setAudio(spec, { sfx });
}
export function setSfx(spec: VideoSpec, i: number, patch: Partial<NonNullable<VideoAudio['sfx']>[number]>): VideoSpec {
  const sfx = [...(spec.audio?.sfx || [])];
  if (!sfx[i]) return spec;
  sfx[i] = { ...sfx[i], ...patch };
  return setAudio(spec, { sfx });
}
export const deleteSfx = (spec: VideoSpec, i: number): VideoSpec => setAudio(spec, { sfx: (spec.audio?.sfx || []).filter((_, k) => k !== i) });
export function addSfx(spec: VideoSpec, url: string, at: number, volume = 0.4): VideoSpec {
  // Linked to the clip under it (it moves with that clip); unlink it to pin it to its time.
  return setAudio(spec, { sfx: [...(spec.audio?.sfx || []), { url, at: r2(at), volume, manual: true, scene: spec.scenes[sceneAt(spec, at)]?.id }].sort((a, b) => a.at - b.at) });
}

/** Snap `t` to the nearest target within `tol` seconds. */
export function snap(t: number, targets: number[], tol: number): number {
  let best = t; let dist = tol;
  for (const x of targets) { const d = Math.abs(x - t); if (d < dist) { dist = d; best = x; } }
  return best;
}

/** "0:03.2" */
export const clock = (t: number): string => { const s = Math.max(0, t); const m = Math.floor(s / 60); const r = s - m * 60; return `${m}:${r < 10 ? '0' : ''}${r.toFixed(1)}`; };

/** The line a scene shows on screen (the Text track). */
export function sceneText(s: VideoScene): string {
  switch (s.kind) {
    case 'hook': case 'text': return s.text;
    case 'clip': return s.overlay || '';
    case 'stat': return `${s.value} ${s.label}`;
    case 'quote': return `“${s.quote}”`;
    case 'product': return s.name;
    case 'endcard': return [s.headline, s.cta && `[${s.cta}]`].filter(Boolean).join(' ');
  }
}


// ── Adding things ──

/** A clip or picture dropped in at index `at`: it plays as a clip scene (pictures get a slow push-in). */
export function addMedia(spec: VideoSpec, media: VideoMedia, at: number, opts: { duration?: number; captions?: Caption[]; voice?: 'keep' | 'mute' } = {}): VideoSpec {
  const scene: VideoScene = {
    id: newSceneId(media.kind === 'video' ? 'clip' : 'picture'), kind: 'clip', duration: r2(opts.duration ?? (media.kind === 'video' ? 2.5 : 2)), transition: 'cut',
    media: { ...media, trimStart: media.kind === 'video' ? media.trimStart || 0 : undefined },
    captions: opts.captions || [], voice: media.kind === 'video' ? opts.voice || 'keep' : 'mute',
  };
  const scenes = [...spec.scenes];
  scenes.splice(Math.max(0, Math.min(at, scenes.length)), 0, scene);
  scenes[0] = { ...scenes[0], transition: 'cut' };
  return withScenes(spec, scenes);
}

export function addTextScene(spec: VideoSpec, at: number, text = 'Your line here'): VideoSpec {
  const prevTone = spec.scenes[at - 1]?.tone;
  const scene: VideoScene = { id: newSceneId('text'), kind: 'text', text, duration: 2, transition: 'cut', tone: prevTone === 'brand' ? 'dark' : 'brand' };
  const scenes = [...spec.scenes];
  scenes.splice(Math.max(0, Math.min(at, scenes.length)), 0, scene);
  return withScenes(spec, scenes);
}

/** Speed: the same moment of the source plays faster (shorter on screen) or slower; captions follow. */
export function setSpeed(spec: VideoSpec, i: number, rate: number): VideoSpec {
  const s = spec.scenes[i];
  const m = mediaOf(s);
  if (m?.kind !== 'video') return spec;
  const old = m.playbackRate || 1;
  const k = old / rate;
  const duration = r2(Math.max(MIN_SCENE, Math.min(MAX_SCENE, s.duration * k)));
  const next = { ...s, duration, media: { ...m, playbackRate: rate === 1 ? undefined : rate } } as VideoScene;
  if (s.kind === 'clip' && s.captions) (next as typeof s).captions = s.captions.map((c) => ({ ...c, start: r2(c.start * k), end: r2(c.end * k) }));
  return withScenes(spec, spec.scenes.map((x, j) => (j === i ? next : x)));
}

/** Several scenes at once (shift-click). */
export function deleteScenes(spec: VideoSpec, indices: number[]): VideoSpec | null {
  const drop = new Set(indices);
  if (drop.size >= spec.scenes.length) return null;
  const scenes = spec.scenes.filter((_, k) => !drop.has(k));
  scenes[0] = { ...scenes[0], transition: 'cut' };
  return withScenes(spec, scenes);
}
export function duplicateScenes(spec: VideoSpec, indices: number[]): VideoSpec {
  const sorted = [...new Set(indices)].sort((a, b) => a - b);
  const copies = sorted.map((k) => { const c = JSON.parse(JSON.stringify(spec.scenes[k])) as VideoScene; c.id = newSceneId(c.id); return c.kind === 'hook' ? ({ id: c.id, kind: 'text', text: c.text, sub: c.sub, duration: c.duration, transition: 'cut', tone: c.tone } as VideoScene) : c; });
  const scenes = [...spec.scenes];
  scenes.splice(sorted[sorted.length - 1] + 1, 0, ...copies);
  return withScenes(spec, scenes);
}

// ── The voice-over track: clips (split, moved, one line per scene) ──

const LEGACY_VO: Partial<VideoAudio> = { voiceoverUrl: null, voiceoverStart: undefined, voiceoverSeconds: undefined, voiceoverScript: undefined, voiceoverVoice: undefined, voiceoverPace: undefined };
/** The track's clips, in time order; the single-take fields are folded in. */
export function setVoParts(spec: VideoSpec, parts: VoicePart[]): VideoSpec {
  const clean = parts.filter((p) => p.seconds > 0.05).map((p) => ({ ...p, at: r2(Math.max(0, p.at)), seconds: r2(p.seconds), ...(p.from ? { from: r2(p.from) } : {}) })).sort((a, b) => a.at - b.at);
  return reflow(setAudio(spec, { ...LEGACY_VO, voParts: clean.length ? clean : undefined }));
}
/** One take on the track (recorded, or a whole AI script); null clears the track. */
export function setVoiceover(spec: VideoSpec, vo: { url: string; start: number; seconds: number; script?: string; voice?: string; pace?: 'calm' | 'normal' | 'fast' } | null): VideoSpec {
  return setVoParts(spec, vo ? [{ id: newSceneId('vo'), url: vo.url, at: vo.start, from: 0, seconds: vo.seconds, text: vo.script, voice: vo.voice, pace: vo.pace }] : []);
}
export const moveVoiceover = (spec: VideoSpec, start: number): VideoSpec => moveVoPart(spec, 0, start);
export function moveVoPart(spec: VideoSpec, i: number, at: number): VideoSpec {
  const parts = [...voicePartsOf(spec.audio)]; if (!parts[i]) return spec;
  const t = Math.max(0, Math.min(lengthSec(spec) - 0.2, at));
  parts[i] = { ...parts[i], at: t, ...(parts[i].scene ? { scene: spec.scenes[sceneAt(spec, t + 0.05)]?.id } : {}) };
  return setVoParts(spec, parts);
}
export function deleteVoPart(spec: VideoSpec, i: number): VideoSpec {
  return setVoParts(spec, voicePartsOf(spec.audio).filter((_, k) => k !== i));
}
/** Cut a clip at `t` (video time): both halves keep playing the same file from where they are. A line's words go with their half, by share. */
export function splitVoPart(spec: VideoSpec, i: number, t: number): VideoSpec | null {
  const parts = [...voicePartsOf(spec.audio)]; const p = parts[i];
  if (!p) return null;
  const cut = t - p.at;
  if (cut < 0.2 || p.seconds - cut < 0.2) return null;
  const words = (p.text || '').split(/\s+/).filter(Boolean);
  const k = Math.round(words.length * (cut / p.seconds));
  const a: VoicePart = { ...p, seconds: cut, text: words.length ? words.slice(0, k).join(' ') || undefined : undefined };
  const b: VoicePart = { ...p, id: newSceneId('vo'), at: t, from: (p.from || 0) + cut, seconds: p.seconds - cut, text: words.length ? words.slice(k).join(' ') || undefined : undefined, scene: undefined };
  parts.splice(i, 1, a, b);
  return setVoParts(spec, parts);
}
/** Snap a clip to the start of the scene it mostly plays over (a beat after the cut, so the voice lands on the picture). */
export function alignVoPart(spec: VideoSpec, i: number): VideoSpec {
  const p = voicePartsOf(spec.audio)[i]; if (!p) return spec;
  const k = sceneAt(spec, p.at + Math.min(p.seconds, 1) / 2);
  return moveVoPart(spec, i, startsSec(spec)[k] + 0.12);
}
/** Every clip to the start of its scene (the wand for a hand-split take). */
export function alignAllVoParts(spec: VideoSpec): VideoSpec {
  let next = spec;
  voicePartsOf(spec.audio).forEach((_, i) => { next = alignVoPart(next, i); });
  return next;
}
/** One line re-made (new take for its text), same place. */
export function replaceVoPart(spec: VideoSpec, i: number, take: { url: string; seconds: number; text: string; voice?: string; pace?: VoicePart['pace'] }): VideoSpec {
  const parts = [...voicePartsOf(spec.audio)]; if (!parts[i]) return spec;
  parts[i] = { ...parts[i], url: take.url, from: 0, seconds: take.seconds, text: take.text, voice: take.voice ?? parts[i].voice, pace: take.pace ?? parts[i].pace };
  return setVoParts(spec, parts);
}

// ── Layers: linked (they move with their clip) or free (they stay at their time) ──

export type LayerKind = 'text' | 'vo' | 'sfx';

/**
 * Linked layers follow their clip: a text, voice-over line or effect tied to a scene keeps its offset from that
 * scene's start when clips move, change length, are inserted or deleted. Its clip deleted → it stays where it was,
 * unlinked. Run after every edit that changes the scenes (prev = the plan the edit started from).
 */
export function followClips(prev: VideoSpec, next: VideoSpec): VideoSpec {
  if (prev.scenes === next.scenes) return next;
  const ps = startsSec(prev); const ns = startsSec(next);
  const was = new Map(prev.scenes.map((x, i) => [x.id, ps[i]]));
  const now = new Map(next.scenes.map((x, i) => [x.id, ns[i]]));
  let changed = false;
  // From where the item stood BEFORE the edit (so running this twice, e.g. after a drag preview, moves it once).
  const keyed = <T extends { scene?: string }>(list: T[], key: (x: T) => string) => { const seen = new Map<string, number>(); return list.map((x) => { const k = key(x); const n = seen.get(k) || 0; seen.set(k, n + 1); return `${k}#${n}`; }); };
  const follow = <T extends { at: number; scene?: string }>(list: T[] | undefined, before: T[] | undefined, key: (x: T) => string): T[] | undefined => {
    if (!list) return list;
    const bk = keyed(before || [], key); const nk = keyed(list, key);
    const old = new Map(bk.map((k, i) => [k, (before || [])[i]]));
    return list.map((x, i) => {
      if (!x.scene) return x;
      const a = was.get(x.scene); const b = now.get(x.scene);
      if (a === undefined) return x;
      if (b === undefined) { changed = true; const { scene: _gone, ...rest } = x; void _gone; return rest as T; }
      if (Math.abs(a - b) < 1e-4) return x;
      const from = old.get(nk[i])?.at ?? x.at;
      const at = r2(Math.max(0, from + (b - a)));
      if (Math.abs(at - x.at) < 1e-4) return x;
      changed = true;
      return { ...x, at };
    });
  };
  const texts = follow(next.texts, prev.texts, (x) => x.id);
  const parts = voicePartsOf(next.audio);
  const movedParts = follow(parts, voicePartsOf(prev.audio), (x) => x.id)!;
  const sfx = follow(next.audio?.sfx, prev.audio?.sfx, (x) => `${x.url}|${x.scene || ''}`);
  if (!changed) return next;
  let out: VideoSpec = { ...next, ...(texts ? { texts } : {}) };
  if (sfx) out = setAudio(out, { sfx: [...sfx].sort((p, q) => p.at - q.at) });
  if (parts.length && next.audio?.voParts) out = setVoParts(out, movedParts);
  return reflow(out);
}

/** Link a layer item to the clip under it, or unlink it (it then stays at its time). */
export function setLinked(spec: VideoSpec, kind: LayerKind, i: number, linked: boolean): VideoSpec {
  const sceneFor = (at: number) => (linked ? spec.scenes[sceneAt(spec, at + 0.05)]?.id : undefined);
  const relink = <T extends { at: number; scene?: string }>(x: T): T => { const { scene: _old, ...rest } = x; void _old; const id = sceneFor(x.at); return (id ? { ...rest, scene: id } : rest) as T; };
  if (kind === 'text') { const texts = [...(spec.texts || [])]; if (!texts[i]) return spec; texts[i] = relink(texts[i]); return { ...spec, texts }; }
  if (kind === 'sfx') { const sfx = [...(spec.audio?.sfx || [])]; if (!sfx[i]) return spec; sfx[i] = relink(sfx[i]); return setAudio(spec, { sfx }); }
  const parts = [...voicePartsOf(spec.audio)]; if (!parts[i]) return spec; parts[i] = relink(parts[i]); return setVoParts(spec, parts);
}

const textsOf = (spec: VideoSpec) => [...(spec.texts || [])];
const withTexts = (spec: VideoSpec, texts: TextLayer[]): VideoSpec => {
  const { texts: _old, ...rest } = spec; void _old;
  const clean = texts.filter((t) => t.text.trim() && t.seconds > 0.05).sort((a, b) => a.at - b.at);
  return clean.length ? { ...rest, texts: clean } : rest;
};

/** A clip's overlay line off its clip, onto its own layer at the same time and place — free to span other clips. */
export function detachText(spec: VideoSpec, i: number): VideoSpec {
  const s = spec.scenes[i];
  if (!s || s.kind !== 'clip' || !s.overlay) return spec;
  const at = startsSec(spec)[i];
  const layer: TextLayer = { id: newSceneId('text'), text: s.overlay, at: r2(at), seconds: r2(s.duration), band: s.place?.overlay || 'top', side: s.place?.side || 'left' };
  const { overlay: _o, ...rest } = s; void _o;
  return withTexts({ ...spec, scenes: spec.scenes.map((x, k) => (k === i ? (rest as VideoScene) : x)) }, [...textsOf(spec), layer]);
}
/** A free text back onto the clip under it as that clip's overlay (when the clip has none) — it then lives and dies with it. */
export function attachText(spec: VideoSpec, k: number): VideoSpec | null {
  const t = spec.texts?.[k]; if (!t) return null;
  const i = sceneAt(spec, t.at + 0.05); const s = spec.scenes[i];
  if (!s || s.kind !== 'clip' || s.overlay) return null;
  const scene = { ...s, overlay: t.text, place: { ...(s.place || {}), overlay: t.band || 'top', side: t.side || 'left' } } as VideoScene;
  return withTexts({ ...spec, scenes: spec.scenes.map((x, n) => (n === i ? scene : x)) }, textsOf(spec).filter((_, n) => n !== k));
}
export function addTextLayer(spec: VideoSpec, at: number, text = 'Your text', seconds = 2): VideoSpec {
  const len = Math.min(seconds, Math.max(0.5, lengthSec(spec) - at));
  return withTexts(spec, [...textsOf(spec), { id: newSceneId('text'), text, at: r2(Math.max(0, at)), seconds: r2(len), band: 'top', side: 'left' }]);
}
export function setTextLayer(spec: VideoSpec, k: number, patch: Partial<TextLayer>): VideoSpec {
  const texts = textsOf(spec); if (!texts[k]) return spec;
  texts[k] = { ...texts[k], ...patch };
  return withTexts(spec, texts);
}
export function moveTextLayer(spec: VideoSpec, k: number, at: number): VideoSpec {
  const t = spec.texts?.[k]; if (!t) return spec;
  const a = r2(Math.max(0, Math.min(lengthSec(spec) - 0.2, at)));
  return setTextLayer(spec, k, { at: a, ...(t.scene ? { scene: spec.scenes[sceneAt(spec, a + 0.05)]?.id } : {}) });
}
/** Drag an edge: `start`/`end` in video seconds (the other edge stays). */
export function trimTextLayer(spec: VideoSpec, k: number, edge: 'start' | 'end', t: number): VideoSpec {
  const x = spec.texts?.[k]; if (!x) return spec;
  const end = x.at + x.seconds;
  if (edge === 'end') return setTextLayer(spec, k, { seconds: r2(Math.max(0.3, Math.min(lengthSec(spec), t) - x.at)) });
  const at = r2(Math.max(0, Math.min(end - 0.3, t)));
  return setTextLayer(spec, k, { at, seconds: r2(end - at) });
}
export const deleteTextLayer = (spec: VideoSpec, k: number): VideoSpec => withTexts(spec, textsOf(spec).filter((_, n) => n !== k));
export function duplicateTextLayer(spec: VideoSpec, k: number): VideoSpec {
  const t = spec.texts?.[k]; if (!t) return spec;
  return withTexts(spec, [...textsOf(spec), { ...t, id: newSceneId('text'), at: r2(Math.min(lengthSec(spec) - 0.3, t.at + t.seconds)) }]);
}

/** Use another part of a clip's source: it starts `from` s into the file and shows `seconds` of it (speed kept). */
export function setClipWindow(spec: VideoSpec, i: number, from: number, seconds: number): VideoSpec {
  const s = spec.scenes[i]; const m = mediaOf(s);
  if (!s || m?.kind !== 'video') return spec;
  const rate = m.playbackRate || 1;
  const next = { ...s, duration: r2(Math.max(MIN_SCENE, Math.min(MAX_SCENE, seconds / rate))), media: { ...m, trimStart: r2(Math.max(0, from)) } } as VideoScene;
  // The creator's captions belong to the old window: the editor listens again for the new one.
  if (next.kind === 'clip') (next as typeof s & { captions?: Caption[] }).captions = [];
  return withScenes(spec, spec.scenes.map((x, k) => (k === i ? next : x)));
}

// ── The captions layer (spec.transcript: per source, in source seconds — see video/captions.ts) ──

/** Every source the video plays (creators' clips, voice-over takes) — what the captions layer needs a transcript of. */
export function captionSources(spec: VideoSpec): Array<{ url: string; kind: 'video' | 'voice' }> {
  const seen = new Map<string, 'video' | 'voice'>();
  for (const s of spec.scenes) { const m = mediaOf(s); if (m?.kind === 'video') seen.set(m.url, 'video'); }
  for (const v of voicePartsOf(spec.audio)) seen.set(v.url, 'voice');
  return [...seen].map(([url, kind]) => ({ url, kind }));
}
/** Sources that still need their transcript. */
export const missingCaptions = (spec: VideoSpec) => captionSources(spec).filter((x) => !spec.transcript?.sources[x.url]);
/** New transcripts in (the layer is on unless the person switched it off; sources already there stay as edited). */
export function mergeTranscripts(spec: VideoSpec, sources: Record<string, TranscriptSource>): VideoSpec {
  return { ...spec, transcript: { on: spec.transcript?.on !== false, sources: { ...sources, ...(spec.transcript?.sources || {}) } } };
}
export const setCaptionsOn = (spec: VideoSpec, on: boolean): VideoSpec => ({ ...spec, transcript: { on, sources: spec.transcript?.sources || {} } });
function withSource(spec: VideoSpec, url: string, fn: (src: TranscriptSource) => TranscriptSource): VideoSpec {
  const src = spec.transcript?.sources[url]; if (!src) return spec;
  return { ...spec, transcript: { on: spec.transcript!.on, sources: { ...spec.transcript!.sources, [url]: fn(src) } } };
}
/** A line's words, as the person wrote them: the timing of the line stays, the words share it by length. */
export function editCaptionLine(spec: VideoSpec, url: string, k: number, text: string): VideoSpec {
  return withSource(spec, url, (src) => {
    const lines = [...src.lines]; const l = lines[k]; if (!l) return src;
    const parts = text.trim().split(/\s+/).filter(Boolean);
    const total = parts.reduce((n, p) => n + p.length + 1, 0) || 1;
    let at = l.s;
    const words = parts.map((w) => { const d = ((w.length + 1) / total) * (l.e - l.s); const x = { w, s: r2(at), e: r2(at + d) }; at += d; return x; });
    lines[k] = { s: l.s, e: l.e, text: text.slice(0, 160), ...(words.length ? { words } : {}) };
    return { ...src, by: 'edited', lines: lines.filter((x) => x.text.trim()) };
  });
}
/** A line's timing (source seconds): its words keep their places relative to the line. */
export function retimeCaptionLine(spec: VideoSpec, url: string, k: number, s: number, e: number): VideoSpec {
  return withSource(spec, url, (src) => {
    const l = src.lines[k]; if (!l) return src;
    const ns = r2(Math.max(0, s)); const ne = r2(Math.max(ns + 0.2, e));
    const scale = (t: number) => r2(ns + ((t - l.s) / Math.max(0.01, l.e - l.s)) * (ne - ns));
    const lines = src.lines.map((x, n) => (n === k ? { ...x, s: ns, e: ne, ...(x.words ? { words: x.words.map((w) => ({ ...w, s: scale(w.s), e: scale(w.e) })) } : {}) } : x)).sort((a, b) => a.s - b.s);
    return { ...src, by: 'edited', lines };
  });
}
/** Cut a line at `t` (source seconds): the words said before go in the first half. */
export function splitCaptionLine(spec: VideoSpec, url: string, k: number, t: number): VideoSpec | null {
  const l = spec.transcript?.sources[url]?.lines[k];
  if (!l || t - l.s < 0.15 || l.e - t < 0.15) return null;
  const words = l.words?.length ? l.words : null;
  const parts = l.text.split(/\s+/).filter(Boolean);
  const cut = words ? words.filter((w) => (w.s + w.e) / 2 < t).length : Math.round(parts.length * ((t - l.s) / (l.e - l.s)));
  if (cut <= 0 || cut >= (words?.length ?? parts.length)) return null;
  const a = { s: l.s, e: r2(t), text: (words ? words.slice(0, cut).map((w) => w.w) : parts.slice(0, cut)).join(' '), ...(words ? { words: words.slice(0, cut) } : {}) };
  const b = { s: r2(t), e: l.e, text: (words ? words.slice(cut).map((w) => w.w) : parts.slice(cut)).join(' '), ...(words ? { words: words.slice(cut) } : {}) };
  return withSource(spec, url, (src) => ({ ...src, by: 'edited', lines: [...src.lines.slice(0, k), a, b, ...src.lines.slice(k + 1)] }));
}
/** A line and the next one of the same source become one. */
export function mergeCaptionLines(spec: VideoSpec, url: string, k: number): VideoSpec | null {
  const src = spec.transcript?.sources[url]; const a = src?.lines[k]; const b = src?.lines[k + 1];
  if (!a || !b) return null;
  const words = a.words && b.words ? [...a.words, ...b.words] : undefined;
  return withSource(spec, url, (x) => ({ ...x, by: 'edited', lines: [...x.lines.slice(0, k), { s: a.s, e: b.e, text: `${a.text} ${b.text}`, ...(words ? { words } : {}) }, ...x.lines.slice(k + 2)] }));
}
/** A caption line the person writes (source seconds) — where the transcript missed something, or for a source with none yet. */
export function addCaptionLine(spec: VideoSpec, url: string, s: number, e: number, text = 'New caption'): VideoSpec {
  const src = spec.transcript?.sources[url] || { lines: [] };
  const lines = [...src.lines, { s: r2(s), e: r2(e), text }].sort((a, b) => a.s - b.s);
  return { ...spec, transcript: { on: spec.transcript?.on !== false, sources: { ...(spec.transcript?.sources || {}), [url]: { ...src, by: 'edited', lines } } } };
}
export const deleteCaptionLine = (spec: VideoSpec, url: string, k: number): VideoSpec => withSource(spec, url, (src) => ({ ...src, by: 'edited', lines: src.lines.filter((_, n) => n !== k) }));
/** Show our captions over a clip that carries the creator's own burned-in subtitles (or hide them again). */
export const setCaptionsHidden = (spec: VideoSpec, url: string, hidden: boolean): VideoSpec => withSource(spec, url, (src) => { const { burned: _b, ...rest } = src; void _b; return hidden ? { ...rest, burned: true } : rest; });

/** A short name for what changed between two plans — the Undo / Redo tooltips and toasts ("split scene 3"). */
export function describeEdit(a: VideoSpec, b: VideoSpec): string {
  const kind = (s: VideoScene) => ({ hook: 'hook', clip: 'clip', text: 'statement', stat: 'number', quote: 'quote', product: 'product', endcard: 'end card' })[s.kind];
  if (a.aspect !== b.aspect) return `format ${b.aspect}`;
  const ids = (s: VideoSpec) => s.scenes.map((x) => x.id);
  const [ia, ib] = [ids(a), ids(b)];
  if (ib.length === ia.length + 1) {
    const k = ib.findIndex((x) => !ia.includes(x));
    const prev = k > 0 ? a.scenes.find((x) => x.id === ib[k - 1]) : null;
    const was = prev ? b.scenes[k - 1] : null;
    if (prev && was && prev.duration !== was.duration && b.scenes[k].kind === (prev.kind === 'hook' ? 'clip' : prev.kind)) return `split scene ${k}`;
    return `added scene ${k + 1} (${kind(b.scenes[k])})`;
  }
  if (ib.length > ia.length) return `added ${ib.length - ia.length} scenes`;
  if (ib.length === ia.length - 1) { const k = ia.findIndex((x) => !ib.includes(x)); return `deleted scene ${k + 1} (${kind(a.scenes[k])})`; }
  if (ib.length < ia.length) return `deleted ${ia.length - ib.length} scenes`;
  if (ia.join() !== ib.join()) return 'moved a scene';
  const changed = b.scenes.map((s, i) => (JSON.stringify(s) !== JSON.stringify(a.scenes[i]) ? i : -1)).filter((i) => i >= 0);
  if (changed.length === 1) {
    const i = changed[0]; const x = a.scenes[i]; const y = b.scenes[i];
    const what = x.duration !== y.duration ? 'trimmed' : (x as { voice?: string }).voice !== (y as { voice?: string }).voice ? ((y as { voice?: string }).voice === 'keep' ? 'unmuted' : 'muted') : x.transition !== y.transition ? 'changed the transition of' : 'edited';
    return `${what} scene ${i + 1} (${kind(y)})`;
  }
  if (changed.length > 1) return `edited ${changed.length} scenes`;
  const [va, vb] = [voicePartsOf(a.audio), voicePartsOf(b.audio)];
  if (vb.length !== va.length) return vb.length > va.length ? (va.length === 0 ? 'added the voice-over' : 'split the voice-over') : vb.length === 0 ? 'removed the voice-over' : 'deleted a voice clip';
  if (JSON.stringify(va) !== JSON.stringify(vb)) return va.some((v, i) => v.url !== vb[i]?.url) ? 'remade the voice' : 'moved the voice-over';
  const [ma, mb] = [a.audio || {}, b.audio || {}];
  if (ma.musicUrl !== mb.musicUrl) return mb.musicUrl ? (ma.musicUrl ? 'changed the music' : 'added music') : 'removed the music';
  if (ma.musicStart !== mb.musicStart) return 'moved the music';
  if (ma.musicVolume !== mb.musicVolume || ma.voiceoverVolume !== mb.voiceoverVolume) return 'changed a level';
  if (a.transcript?.on !== b.transcript?.on) return b.transcript?.on ? 'captions on' : 'captions off';
  if (JSON.stringify(a.transcript) !== JSON.stringify(b.transcript)) return 'edited captions';
  if (JSON.stringify(a.texts) !== JSON.stringify(b.texts)) return (b.texts?.length || 0) > (a.texts?.length || 0) ? 'added a text layer' : (b.texts?.length || 0) < (a.texts?.length || 0) ? 'removed a text layer' : 'changed a text layer';
  if (JSON.stringify(ma.sfx) !== JSON.stringify(mb.sfx)) return (mb.sfx?.length || 0) > (ma.sfx?.length || 0) ? 'added a sound effect' : (mb.sfx?.length || 0) < (ma.sfx?.length || 0) ? 'removed a sound effect' : 'moved a sound effect';
  if (JSON.stringify(ma.duck) !== JSON.stringify(mb.duck)) return 'changed the music dips';
  return 'the last change';
}
